/**
 * Build the exhaustive list of UILM keys that should exist for a system
 * template. ONE pure function — every phase that needs to seed, diff, or
 * sync this template's translation footprint calls this and walks the
 * result.
 *
 * Output entries describe both:
 *   - what the key NAME should be (so it can be saved to UILM), AND
 *   - which UDS record + field needs to be populated with that key
 *     string (so consumers can look up the key without re-computing
 *     it from the formula every time).
 */

import { uilmKeyForTemplate } from '@/i18n/utils/uilm-key-for-template';
import { uilmKeyForScreen } from '@/i18n/utils/uilm-key-for-screen';
import { uilmKeyForField } from '@/i18n/utils/uilm-key-for-field';
import { uilmKeyForOption } from '@/i18n/utils/uilm-key-for-option';
import type {
  Template,
  TemplateField,
  TemplateScreen,
} from '@/modules/templates/types/templates.types';

export type KeyOwnerType = 'template' | 'screen' | 'field' | 'option';

/**
 * The UDS field name that should store this key string. Options are a
 * special case — they go into the parent field's `uilmOptionsKeys[]`
 * array at the position indicated by `optionIndex`.
 */
export type KeyUilmField =
  | 'uilmTitleKey'
  | 'uilmDescriptionKey'
  | 'uilmLabelKey'
  | 'uilmHelpTextKey'
  | 'uilmPlaceholderKey'
  | 'uilmOptionsKeys';

export interface KeyPlanEntry {
  /** The UILM key name we'll push, e.g. `NDA_SCR_SA3F2B1C8_TITLE`. */
  keyName: string;
  /** The current EN value from the local DB — pushed as the `en-US` resource AND used as the runtime fallback. */
  enValue: string;
  ownerType: KeyOwnerType;
  /**
   * UDS ItemId of the entity that should remember this key string. For
   * options this is the parent FIELD id (the field owns the keys array).
   */
  ownerId: string;
  uilmField: KeyUilmField;
  /** Position in `options[]` — only set when `ownerType === 'option'`. */
  optionIndex?: number;
}

export interface BuildTemplateKeysInput {
  template: Pick<Template, 'id' | 'slug' | 'title' | 'description'>;
  screens: Array<
    Pick<TemplateScreen, 'id' | 'title' | 'description'> & {
      fields: Pick<
        TemplateField,
        'id' | 'label' | 'helpText' | 'placeholder' | 'type' | 'options'
      >[];
    }
  >;
}

export function buildTemplateKeys({ template, screens }: BuildTemplateKeysInput): KeyPlanEntry[] {
  const plan: KeyPlanEntry[] = [];

  // --- Template-level keys ---
  plan.push({
    keyName: uilmKeyForTemplate(template.slug, 'title'),
    enValue: template.title ?? '',
    ownerType: 'template',
    ownerId: template.id,
    uilmField: 'uilmTitleKey',
  });
  if (template.description) {
    plan.push({
      keyName: uilmKeyForTemplate(template.slug, 'desc'),
      enValue: template.description,
      ownerType: 'template',
      ownerId: template.id,
      uilmField: 'uilmDescriptionKey',
    });
  }

  // --- Screen + field keys ---
  for (const screen of screens) {
    plan.push({
      keyName: uilmKeyForScreen(template.slug, screen.id, 'title'),
      enValue: screen.title ?? '',
      ownerType: 'screen',
      ownerId: screen.id,
      uilmField: 'uilmTitleKey',
    });
    if (screen.description) {
      plan.push({
        keyName: uilmKeyForScreen(template.slug, screen.id, 'desc'),
        enValue: screen.description,
        ownerType: 'screen',
        ownerId: screen.id,
        uilmField: 'uilmDescriptionKey',
      });
    }

    for (const field of screen.fields) {
      plan.push({
        keyName: uilmKeyForField(template.slug, screen.id, field.id, 'label'),
        enValue: field.label ?? '',
        ownerType: 'field',
        ownerId: field.id,
        uilmField: 'uilmLabelKey',
      });
      if (field.helpText) {
        plan.push({
          keyName: uilmKeyForField(template.slug, screen.id, field.id, 'help'),
          enValue: field.helpText,
          ownerType: 'field',
          ownerId: field.id,
          uilmField: 'uilmHelpTextKey',
        });
      }
      if (field.placeholder) {
        plan.push({
          keyName: uilmKeyForField(template.slug, screen.id, field.id, 'placeholder'),
          enValue: field.placeholder,
          ownerType: 'field',
          ownerId: field.id,
          uilmField: 'uilmPlaceholderKey',
        });
      }

      // Options — only for select / multiselect, and the option string
      // itself is the en-US value (admins author options in English).
      if (
        (field.type === 'select' || field.type === 'multiselect') &&
        Array.isArray(field.options)
      ) {
        field.options.forEach((opt, index) => {
          if (!opt) return;
          plan.push({
            keyName: uilmKeyForOption(template.slug, screen.id, field.id, opt),
            enValue: opt,
            ownerType: 'option',
            ownerId: field.id, // parent field owns `uilmOptionsKeys[]`
            uilmField: 'uilmOptionsKeys',
            optionIndex: index,
          });
        });
      }
    }
  }

  return plan;
}
